import Link from 'next/link';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, BadgeCheck, ChevronLeft, ChevronRight, ChevronsRight, MoveUpRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { Autoplay } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import { useI18n } from '../../context/I18nContext';
import AnimatedEyebrow from '../AnimatedEyebrow';
import ImageOnlySkeletonLoader from '../ImageOnlySkeletonLoader';
import HomeCtaSection from './HomeCtaSection';
import { getHomeData } from './data';
import { localizePath } from '../../lib/localizedRoutes';

const HERO_AUTOPLAY_MS = 5000;
const HERO_DESKTOP_SLIDES = 3.5;
const HERO_DESKTOP_GAP = 24;

function getBaseIndex(swiper, baseCount) {
  const rawIndex = Number.isFinite(swiper?.realIndex) ? swiper.realIndex : (swiper?.activeIndex || 0);
  return baseCount ? rawIndex % baseCount : 0;
}

function toFiniteNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function extractCapacityNumbers(value) {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? [value] : [];
  }

  const text = String(value || '').trim();
  if (!text) return [];

  const normalized = text.replace(/,/g, '.');
  const matches = normalized.match(/\d+(?:\.\d+)?/g) || [];
  if (!matches.length) return [];

  const usesTons = /\bton\b|\btons\b|\bt\b/i.test(normalized);

  return matches
    .map((token) => {
      const numeric = Number(token);
      if (!Number.isFinite(numeric)) return null;
      if (usesTons && numeric <= 20) return numeric * 1000;
      return numeric;
    })
    .filter((numeric) => numeric !== null);
}

function formatCapacityRange(values = []) {
  const numeric = values.flatMap((value) => extractCapacityNumbers(value)).filter((value) => value !== null);
  if (!numeric.length) return null;
  const min = Math.min(...numeric);
  const max = Math.max(...numeric);
  const minTons = (min / 1000).toFixed(min >= 10000 ? 0 : 1);
  const maxTons = (max / 1000).toFixed(max >= 10000 ? 0 : 1);
  return min === max ? `${minTons}t` : `${minTons}-${maxTons}t`;
}

function normalizePowerLabel(value = '', t) {
  const text = String(value || '').trim();
  if (!text) return '';
  const lower = text.toLowerCase();
  if (lower.includes('electric') || lower.includes('battery') || lower.includes('li-ion')) {
    return t('home.hero.fuelElectric');
  }
  if (lower.includes('diesel')) return t('home.hero.fuelDiesel');
  if (lower.includes('lpg') || lower.includes('gas')) return t('home.hero.fuelLpg');
  return text.length > 14 ? `${text.slice(0, 14)}...` : text;
}

function inferLeadTimeDays(product = {}) {
  const specs = product?.specs && typeof product.specs === 'object' ? product.specs : {};
  const dayCandidates = [
    specs.lead_time_days,
    specs.delivery_days,
    specs.delivery_time_days,
    specs.lead_time,
    specs.delivery_lead_time,
    specs.delivery_time,
  ]
    .map(toFiniteNumber)
    .filter((value) => value !== null && value >= 0);
  return dayCandidates.length > 0 ? dayCandidates[0] : null;
}

async function fetchJsonOrThrow(url) {
  const response = await fetch(url);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error('Request failed');
  }
  return data;
}

export default function HomeHeroSection() {
  const { language, t } = useI18n();
  const { categoryCards, heroSlides } = getHomeData(language);
  const leadSlide = heroSlides?.[0];
  const [dynamicCategoryCards, setDynamicCategoryCards] = useState([]);
  const cards = useMemo(() => {
    if (Array.isArray(dynamicCategoryCards) && dynamicCategoryCards.length > 0) {
      return dynamicCategoryCards;
    }
    return Array.isArray(categoryCards) ? categoryCards : [];
  }, [categoryCards, dynamicCategoryCards]);
  const renderCards = useMemo(() => {
    if (!cards.length) return [];
    if (cards.length === 1) return cards;
    if (cards.length >= 5) return cards;
    return [...cards, ...cards, ...cards];
  }, [cards]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const swiperRef = useRef(null);
  const glowRef = useRef(null);
  const glowRafRef = useRef(null);
  const cardsCount = cards.length;

  const handleSectionMouseMove = (event) => {
    if (glowRafRef.current) return;
    const clientX = event.clientX;
    const clientY = event.clientY;
    const rect = event.currentTarget.getBoundingClientRect();
    glowRafRef.current = requestAnimationFrame(() => {
      glowRafRef.current = null;
      const x = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
      const y = Math.max(0, Math.min(100, ((clientY - rect.top) / rect.height) * 100));
      if (glowRef.current) {
        glowRef.current.style.backgroundImage = `radial-gradient(circle at ${x}% ${y}%, rgba(255,93,16,0.08) 0%, transparent 40%)`;
      }
    });
  };

  useEffect(() => {
    setCurrentIndex(0);
    if (swiperRef.current) {
      swiperRef.current.slideTo(0, 0);
    }
  }, [language]);

  const handlePrevClick = () => {
    const swiper = swiperRef.current;
    if (!swiper || cardsCount <= 1) return;
    swiper.slidePrev();
  };

  const handleNextClick = () => {
    const swiper = swiperRef.current;
    if (!swiper || cardsCount <= 1) return;
    swiper.slideNext();
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.16,
        delayChildren: 0.12,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 28 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
    },
  };

  useEffect(() => {
    let isMounted = true;

    Promise.all([
      fetchJsonOrThrow(`/api/products?lang=${language}`),
      fetchJsonOrThrow('/api/product-types').catch(() => []),
    ])
      .then(([productsData, typeData]) => {
        if (!isMounted) return;
        const products = Array.isArray(productsData) ? productsData : [];
        const types = Array.isArray(typeData) ? typeData : [];
        const stats = new Map();

        products.forEach((product) => {
          const key = String(product?.productType || '').trim() || 'unclassified';
          if (key === 'unclassified') return;
          if (!stats.has(key)) {
            stats.set(key, {
              key,
              count: 0,
              image: product?.images?.[0] || '',
            });
          }
          const entry = stats.get(key);
          entry.count += 1;
          if (!entry.image && product?.images?.[0]) {
            entry.image = product.images[0];
          }
        });

        const mapped = Array.from(stats.values())
          .map((entry) => {
            const typeMeta = types.find((type) => type.key === entry.key);
            const label = typeMeta?.labelI18n?.[language] || typeMeta?.label || entry.key;
            const typeProducts = products.filter(
              (product) => String(product?.productType || '').trim() === entry.key
            );
            const capacities = typeProducts
              .map((product) => {
                const specs = product?.specs && typeof product.specs === 'object' ? product.specs : {};
                return specs.loading_capacity ?? specs.capacity ?? null;
              })
              .filter((value) => value !== null && value !== undefined && value !== '');
            const powerSet = new Set();
            const leadDays = [];

            typeProducts.forEach((product) => {
              const specs = product?.specs && typeof product.specs === 'object' ? product.specs : {};
              const powerValues = [specs.power_type, specs.battery, specs.engine_model];
              powerValues.forEach((value) => {
                const normalized = normalizePowerLabel(value, t);
                if (normalized) powerSet.add(normalized);
              });

              const lead = inferLeadTimeDays(product);
              if (lead !== null) leadDays.push(lead);
            });
            const leadMin = leadDays.length ? Math.min(...leadDays) : 0;
            const leadMax = leadDays.length ? Math.max(...leadDays) : 0;

              return {
              title: label,
              count: entry.count,
              description: `${entry.count} ${t('productsPage.filters.products')}`,
              href: `/products?productType=${encodeURIComponent(entry.key)}&productTypeLabel=${encodeURIComponent(label)}`,
              image: typeMeta?.imageUrl || entry.image || '/placeholders/1.webp',
              typeBadge: label,
              capacityChip: formatCapacityRange(capacities),
              powerChip: powerSet.size ? Array.from(powerSet).slice(0, 2).join(' / ') : null,
              leadTime:
                leadMax > 0
                  ? leadMin === leadMax
                    ? t('home.hero.leadTimeSingle', { count: leadMin })
                    : t('home.hero.leadTimeRange', { min: leadMin, max: leadMax })
                  : null,
            };
          })
          .sort((a, b) => b.count - a.count)
          .slice(0, 8);

        setDynamicCategoryCards(mapped);
      })
      .catch(() => {
        if (!isMounted) return;
        setDynamicCategoryCards([]);
      });

    return () => {
      isMounted = false;
    };
  }, [language, t]);

  return (
    <section
      className="relative overflow-x-hidden bg-[var(--dash-blacker)] pt-20 pb-14 md:pt-24 md:pb-18"
      onMouseMove={handleSectionMouseMove}
    >
      <div className="pointer-events-none absolute inset-0 z-0">
        <div className="absolute left-0 top-0 h-full w-full bg-[radial-gradient(circle_at_15%_25%,rgba(255,93,16,0.08)_0%,transparent_40%)]" />
      </div>
      <div className="pointer-events-none absolute inset-0 z-[1]">
        <div
          ref={glowRef}
          className="absolute left-0 top-0 h-full w-full transition-[background-image] duration-150 ease-out"
          style={{ backgroundImage: 'radial-gradient(circle at 15% 25%, rgba(255,93,16,0.08) 0%, transparent 40%)' }}
        />
      </div>
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-0 bg-cover bg-center opacity-20"
        style={{ backgroundImage: leadSlide?.image ? `url(${leadSlide.image})` : 'none' }}
      />
      <div className="pointer-events-none absolute inset-0 z-0 opacity-20 mix-blend-screen bg-[url('/bg/5.webp')] bg-no-repeat bg-cover bg-center" />

      <div className="container relative z-10 mx-auto px-6 py-24 lg:px-12 lg:py-0">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-120px' }}
          className="grid items-center gap-12 lg:grid-cols-12 lg:gap-16"
        >
          <motion.div variants={itemVariants} className="z-20 flex flex-col lg:col-span-5">
            <motion.div key={language} variants={containerVariants} className="flex flex-col">
              <motion.div variants={itemVariants} className="mb-6">
                <AnimatedEyebrow>{t('home.hero.eyebrow')}</AnimatedEyebrow>
              </motion.div>

              <motion.h1 variants={itemVariants} className="text-4xl font-light uppercase leading-tight text-white sm:text-5xl">
                {t('home.hero.titleLine1')}
                <span className="block font-black bg-gradient-to-r from-orange-400 via-[var(--dash-main)] to-orange-700 bg-clip-text text-transparent">
                  {t('home.hero.titleLine2')}
                </span>
              </motion.h1>

              <motion.p variants={itemVariants} className="mt-6 max-w-md text-base font-light leading-relaxed text-slate-400 lg:mt-8 lg:text-lg">
                {leadSlide?.description}
              </motion.p>

              <motion.div variants={itemVariants} className="mt-10 flex items-center gap-8">
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handlePrevClick}
                    className="group relative flex h-16 w-16 items-center justify-center overflow-hidden rounded-full"
                    aria-label={t('home.hero.prevMedia')}
                  >
                    <div className="absolute inset-0 scale-100 rounded-full bg-white/10 transition-all duration-700 group-hover:scale-150 group-hover:opacity-0" />
                    <div className="absolute inset-0 scale-0 rounded-full bg-[var(--dash-other)] transition-transform duration-500 group-hover:scale-100" />
                    <ChevronLeft className="relative z-10 h-6 w-6 text-white transition-colors group-hover:text-[var(--dash-black)]" />
                  </button>
                  <button
                    type="button"
                    onClick={handleNextClick}
                    className="group relative flex h-16 w-16 items-center justify-center overflow-hidden rounded-full"
                    aria-label={t('home.hero.nextMedia')}
                  >
                    <div className="absolute inset-0 scale-100 rounded-full bg-white/10 transition-all duration-700 group-hover:scale-150 group-hover:opacity-0" />
                    <div className="absolute inset-0 scale-0 rounded-full bg-[var(--dash-other)] transition-transform duration-500 group-hover:scale-100" />
                    <ChevronRight className="relative z-10 h-6 w-6 text-white transition-colors group-hover:text-[var(--dash-black)]" />
                  </button>
                </div>

                <div className="flex gap-1.5">
                  {cards.map((_, idx) => (
                    <div
                      key={`progress-${idx}`}
                      className={`h-1 rounded-full transition-all duration-500 ${
                        currentIndex === idx ? 'w-8 bg-[#ff5d10]' : 'w-2 bg-white/20'
                      }`}
                    />
                  ))}
                </div>
              </motion.div>

              <motion.div variants={itemVariants} className="mt-12 flex flex-wrap gap-5">
                <Link
                  href={localizePath(leadSlide?.primaryHref || '/machines/categories', language)}
                  className="group relative inline-flex items-center gap-3 overflow-hidden rounded-full bg-[var(--dash-main)] px-8 py-4 text-sm font-bold text-white transition-all hover:bg-[var(--dash-other)] hover:pr-14 hover:text-[var(--dash-black)] active:scale-95"
                >
                  <span className="relative z-10">{leadSlide?.primaryLabel}</span>
                  <ArrowRight className="absolute right-5 h-5 w-5 translate-x-8 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
                </Link>
              </motion.div>
            </motion.div>
          </motion.div>

          <motion.div variants={itemVariants} className="relative mt-8 h-[400px] w-full sm:h-[500px] lg:col-span-7 lg:mt-0 lg:h-[600px]">
            <div className="absolute inset-0 overflow-hidden lg:-mr-[100vw]">
              {!cardsCount ? (
                <div className="flex h-full w-full items-center justify-center rounded-[2rem] border border-white/10 bg-[#111116] text-sm text-slate-400">
                  {t('home.hero.noSlidesAvailable')}
                </div>
              ) : (
              <Swiper
                modules={[Autoplay]}
                onSwiper={(swiper) => {
                  swiperRef.current = swiper;
                  setCurrentIndex(getBaseIndex(swiper, cardsCount));
                }}
                onSlideChange={(swiper) => setCurrentIndex(getBaseIndex(swiper, cardsCount))}
                speed={1000}
                grabCursor
                allowTouchMove={cardsCount > 1}
                loop={cardsCount > 1}
                watchSlidesProgress
                slidesPerView={1}
                spaceBetween={0}
                autoplay={
                  cardsCount > 1
                    ? {
                        delay: HERO_AUTOPLAY_MS,
                        disableOnInteraction: false,
                        pauseOnMouseEnter: true,
                      }
                    : false
                }
                breakpoints={{
                  1024: {
                    slidesPerView: HERO_DESKTOP_SLIDES,
                    spaceBetween: HERO_DESKTOP_GAP,
                  },
                }}
                className="h-full w-full select-none lg:touch-pan-y"
              >
                {renderCards.map((card, idx) => (
                  <SwiperSlide key={`${card.title}-${idx}`} className="h-full">
                    {({ isActive }) => {
                      const cardClassName = `group relative block h-full overflow-hidden rounded-[2.5rem] border border-white/10 bg-[#121214] shadow-[0_24px_60px_-28px_rgba(0,0,0,0.7)] transition-all duration-700 scale-[0.95] ${
                        isActive
                          ? 'scale-100 opacity-100 hover:scale-[1.0]'
                          : 'pointer-events-none scale-[0.85] opacity-100 blur-[2px]'
                      }`;
                      const cardBody = (
                        <>
                        <div className="absolute inset-0 z-0 bg-zinc-800">
                          <ImageOnlySkeletonLoader
                            src={card.image}
                            mode="native"
                            alt={card.title}
                            loading={idx === 0 ? 'eager' : 'lazy'}
                            fetchPriority={idx === 0 ? 'high' : 'auto'}
                            containerClassName="h-full w-full"
                            imgClassName="h-full w-full object-cover opacity-100 transition-all duration-[1.5s] ease-out group-hover:scale-110 group-hover:opacity-90"
                          />
                          <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/10 to-transparent transition-transform duration-1000 ease-in-out group-hover:translate-x-full" />
                        </div>
                        <div className="absolute inset-0 z-10 bg-gradient-to-t from-zinc-950 via-zinc-950/20 to-transparent opacity-80" />
                        <div className="absolute inset-0 z-10 bg-[rgba(var(--dash-main-rgb),0.08)] opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                        <div className="absolute inset-0 z-20 p-10">
                          <div className="flex h-full flex-col justify-between">
                            <div className="flex justify-start">
                              <span className="inline-flex items-center rounded-full bg-[var(--dash-main)] px-3.5 py-1.5 text-[12px] font-semibold tracking-[0.06em] text-white/95 backdrop-blur-sm shadow-[0_10px_24px_rgba(0,0,0,0.25)] transition-all duration-500 group-hover:bg-[var(--dash-main)] group-hover:text-white group-hover:shadow-[0_12px_28px_rgba(var(--dash-main-rgb),0.25)]">
                                <span>{card.typeBadge || card.title}</span>
                              </span>
                            </div>

                            <div className={`space-y-6 transition-all ${isActive ? 'translate-y-0 opacity-100 duration-500' : 'translate-y-4 opacity-0 duration-300'}`}>
                              <div className="transition-transform duration-500 group-hover:-translate-y-2">
                                <div className="mb-4 inline-flex items-center gap-2 rounded-full text-[var(--dash-black)] bg-[var(--dash-other)] px-5 py-2 text-[12px] font-[600] tracking-[0.08em] transition-all duration-500 group-hover:border-[var(--dash-other)] group-hover:bg-[var(--dash-other)] group-hover:shadow-[0_10px_24px_rgba(255,93,16,0.2)]">
                                  <span>{card.description || t('productsPage.filters.products')}</span>
                                </div>
                                <h3 className="text-3xl font-[700] uppercase leading-[1] tracking-[-0.01em] text-white drop-shadow-2xl md:text-3xl">
                                  {card.title}
                                </h3>
                                {card.capacityChip || card.powerChip ? (
                                  <div className="mt-4 flex flex-wrap gap-2">
                                    {card.capacityChip ? (
                                      <span className="rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-white/90">
                                        {card.capacityChip}
                                      </span>
                                    ) : null}
                                  </div>
                                ) : null}
                                {card.leadTime ? <p className="mt-1 text-xs text-white/70">{card.leadTime}</p> : null}
                              </div>

                              <div className="flex items-center justify-between border-t border-white/20 pt-6">
                                <span className="group/link flex items-center gap-2 text-sm font-bold text-zinc-300 transition-colors duration-300 group-hover:text-white">
                                  <span className="relative">
                                    {t('home.hero.completeCollection')}
                                    <span className="absolute -bottom-1.5 left-0 h-0.5 w-0 bg-[var(--dash-main)] transition-all duration-300 group-hover:w-full" />
                                  </span>
                                  <MoveUpRight size={16} className="transition-transform duration-300 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5" />
                                </span>
                                <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[var(--dash-other)] text-black transition-all duration-500 group-hover:bg-[var(--dash-main)] group-hover:text-white group-hover:shadow-[0_0_40px_rgba(var(--dash-main-rgb),0.55)] active:scale-90">
                                  <ChevronsRight size={20} strokeWidth={3} />
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                        </>
                      );
                      return (
                        <Link
                          href={localizePath(card.href || '/machines/categories', language)}
                          className={cardClassName}
                          tabIndex={isActive ? 0 : -1}
                          aria-disabled={!isActive}
                          onClick={(event) => {
                            if (isActive) return;
                            event.preventDefault();
                          }}
                        >
                          {cardBody}
                        </Link>
                      );
                    }}
                  </SwiperSlide>
                ))}
              </Swiper>
              )}
            </div>
          </motion.div>
        </motion.div>
      </div>
      <div className="relative z-10">
        <HomeCtaSection sharedSurface />
      </div>
    </section>
  );
}
